import { listClients } from "@/app/actions/admin/client-actions";
import { listUsers } from "@/app/actions/admin/user-actions";
import { searchDocumentsByName } from "@/app/actions/document-search-actions";
import { createClientLogger } from "@/lib/client-logger";

const log = createClientLogger("palette-search");

export const DEBOUNCE_MS = 250;
/** Minimum characters before hitting the search actions. */
export const MIN_SEARCH_LENGTH = 2;
/** Max results shown per palette group. */
export const MAX_RESULTS = 8;

export interface ClientHit {
  id: string;
  name: string;
}

export interface UserHit {
  id: string;
  name: string | null;
  email: string;
  clientId: string | null;
  clientName: string | null;
}

export interface ChatHit {
  id: string;
  title: string;
  snippet: string;
}

export interface DocumentHit {
  id: string;
  name: string;
  category: string | null;
}

export interface PaletteSearchResults {
  clients: ClientHit[];
  users: UserHit[];
  chats: ChatHit[];
  documents: DocumentHit[];
}

/** SWR key for one debounced palette search: query + the caller's scopes. */
export type PaletteSearchKey = [
  tag: string,
  search: string,
  superAdmin: boolean,
  admin: boolean,
  scopedClientId: string | null,
  includeChats: boolean,
];

/**
 * The user's own chats, via the same literal-match endpoint that backs the
 * sidebar's chat search dialog (GET /api/history/search — ILIKE over message
 * bodies, ownership verified server-side). Failures degrade to no results,
 * with a console breadcrumb (AIR-1083) — fully silent failures made a source
 * outage indistinguishable from "no matches".
 */
async function fetchChatHits(search: string): Promise<ChatHit[]> {
  try {
    const response = await fetch(
      `/api/history/search?q=${encodeURIComponent(search)}&limit=${MAX_RESULTS}`,
    );
    if (!response.ok) {
      log.warn("chat search failed", { status: response.status });
      return [];
    }
    const data = (await response.json()) as { chats?: ChatHit[] };
    return (data.chats ?? [])
      .slice(0, MAX_RESULTS)
      .map((chat) => ({ id: chat.id, title: chat.title, snippet: chat.snippet }));
  } catch (error) {
    log.warn("chat search failed", { error });
    return [];
  }
}

/** Visible documents by filename, via the auth-gated server action. */
async function fetchDocumentHits(search: string): Promise<DocumentHit[]> {
  try {
    const result = await searchDocumentsByName({ search, limit: MAX_RESULTS });
    if (!result.success) {
      log.warn("document search failed", { error: result.error });
      return [];
    }
    return result.data.map((doc) => ({ id: doc.id, name: doc.name, category: doc.category }));
  } catch (error) {
    log.warn("document search failed", { error });
    return [];
  }
}

/**
 * Fetch every palette search group in one round of parallel requests
 * (AIR-611 admin quick-jump + AIR-989 content search). All matching is
 * literal/ILIKE server-side — zero model calls. Auth is enforced server-side
 * per source; any failed source degrades to an empty group (leaving a
 * console breadcrumb, AIR-1083) rather than surfacing an error in the
 * palette.
 */
export async function fetchPaletteSearchResults([
  ,
  search,
  superAdmin,
  admin,
  scopedClientId,
  includeChats,
]: PaletteSearchKey): Promise<PaletteSearchResults> {
  const params = {
    page: 1,
    limit: MAX_RESULTS,
    search,
    sortBy: "name",
    sortOrder: "asc" as const,
  };

  const [clientsResult, usersResult, chats, documents] = await Promise.all([
    superAdmin
      ? listClients(params).catch((error: unknown) => {
          log.warn("client search failed", { error });
          return null;
        })
      : Promise.resolve(null),
    admin
      ? listUsers({ ...params, clientId: scopedClientId ?? undefined }).catch((error: unknown) => {
          log.warn("user search failed", { error });
          return null;
        })
      : Promise.resolve(null),
    includeChats ? fetchChatHits(search) : Promise.resolve([]),
    fetchDocumentHits(search),
  ]);

  return {
    clients: clientsResult?.success
      ? clientsResult.data.data.map((c) => ({ id: c.id, name: c.name }))
      : [],
    users: usersResult?.success
      ? usersResult.data.data.map((u) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          clientId: u.clientId,
          clientName: u.client?.name ?? null,
        }))
      : [],
    chats,
    documents,
  };
}
